'use client';
import cn from 'classnames';
import { useRouter } from 'next/navigation';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { filterVipFromInsuranceCatalog } from '@/util/vip-insurance';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import './SignUpApp.style.scss';

import { getUtmSource } from '@/util/utm_client';
import { useOnMount } from '@/components/hook/onMount';
import { useSignup } from '@/components/hook/signup';
import {
  useAmbassadorRedirectFlag,
  useExperiment
} from '@/components/hook/flags';
import { FLAG_KEYS } from '@/const/flags';
import SignupForm from '@/components/form/SignupForm/SignupForm';
import SignUpSummaryBar from '@/components/common/SignUpSummaryBar/SignUpSummaryBar';
import { InsuranceProduct, SubscriptionProduct } from '@/types/Subscription';
import {
  createPreviewSubscriptionCart,
  PreviewCartRequest
} from '@/util/user_client';
import analytics from '@/util/analytics';
import { initializeDatadogRUM } from '@/util/datadog';
import { SIGNUP_ROUTES } from '@/const/signup';
import { isBrandAmbassadorCode } from '@/util/form_validation_errors';
import { stripeOptions } from '@/const/stripe';
import { RawEPCCCartResponse } from '@/types/Cart';
import { NextSearchParams } from '@/types/Next';
import { APPLICATION_FLOW, ApplicationFlow } from '@/const/application_flow';

import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';

type SignUpAppProps = {
  className?: string;
  subscriptionData: SubscriptionProduct[];
  insuranceData: InsuranceProduct[];
  searchParams?: NextSearchParams;
  stepIndex?: number;
  /**
   * Which signup experience the visitor is in. `/apply/review` is shared with
   * the single-page flow, so this cannot be inferred here — the route passes it.
   */
  applicationFlow?: ApplicationFlow;
};

export type ReferralCopyProps = {
  copy: ReactNode;
  successfullyApplied: boolean;
};

const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || ''
);

function SignUpApp(props: SignUpAppProps) {
  const {
    className = '',
    subscriptionData,
    insuranceData,
    searchParams,
    stepIndex = 0,
    applicationFlow = APPLICATION_FLOW.MULTI_PAGE
  } = props;

  const router = useRouter();
  const { user } = useContextBroker();
  const { value: state, actions: signupActions } = useSignup();
  const ambassadorRedirectModalEnabled = useAmbassadorRedirectFlag();
  // Starts the /apply single-page vs. multi-step test clock (exposure only —
  // the one_page UI doesn't exist yet, so this doesn't change rendering).
  useExperiment(FLAG_KEYS.APPLY_SINGLE_PAGE);
  const eligibleInsuranceData = useMemo(
    () => filterVipFromInsuranceCatalog(insuranceData),
    [insuranceData]
  );
  const [previewCart, setPreviewCart] = useState<RawEPCCCartResponse>();
  const [previewCartUpdated, setPreviewCartUpdated] = useState<boolean>(false);
  const [stripeCouponId, setStripeCouponId] = useState<string | undefined>();
  const [requiresAuthorizationCharge, setRequiresAuthorizationCharge] =
    useState<boolean | undefined>();

  useEffect(() => {
    if (user) {
      router.replace('/account');
    }
  }, [user, router]);

  const referralCopy = useMemo(() => {
    let referral: ReferralCopyProps = {
      copy: '',
      successfullyApplied: false
    };

    // Ambassador codes never apply here — direct the user to the ambassador flow
    if (
      ambassadorRedirectModalEnabled &&
      isBrandAmbassadorCode(state.referralCode)
    ) {
      return referral;
    }

    if (state.referralCode && state.referralCode.trim() && previewCartUpdated) {
      if (previewCart?.included?.promotions) {
        // Centralizing the copy below so there less state/DOM update happening in setp1.
        // If we need to return the specific copy for the promo code,
        // we can refer to previewCart?.included?.promotions[0].name
        referral.copy = `Success! ${state.referralCode} applied!`;
        referral.successfullyApplied = true;
      } else {
        referral.copy = 'Applied after acceptance';
      }

      return referral;
    }
  }, [
    ambassadorRedirectModalEnabled,
    previewCart?.included?.promotions,
    previewCartUpdated,
    state.referralCode
  ]);

  useEffect(() => {
    const createPreviewCart = async () => {
      // Compile membership selections
      const basePlan = subscriptionData.find(
        (plan) => plan.slug === state.plan?.slug
      );
      const addonPlan = subscriptionData.find(
        (plan) => plan.slug === state.addon_subscription
      );
      const basePlanId = basePlan?.productId;
      const addonPlanId = addonPlan?.productId;

      const insuranceProductId = eligibleInsuranceData.find(
        (plan) => plan.slug === state.insurance_level
      )?.id;
      setPreviewCartUpdated(false);

      // No cart to create without subscription products
      if (!basePlanId || !insuranceProductId) {
        return;
      }

      let subscriptionProductIds = [basePlanId];

      if (addonPlanId) {
        subscriptionProductIds.push(addonPlanId);
      }

      const promotionCodes = [];

      // Ambassador codes never apply in this flow — don't preview their discount
      if (
        state.referralCode &&
        !(
          ambassadorRedirectModalEnabled &&
          isBrandAmbassadorCode(state.referralCode)
        )
      ) {
        promotionCodes.push(state.referralCode);
      }

      const cartResponse = await createPreviewSubscriptionCart({
        insuranceProductId,
        promotionCodes,
        subscriptionProductIds
      });

      if (cartResponse) {
        setPreviewCart(cartResponse.cart);
        setStripeCouponId(cartResponse.stripeCouponId);
        setRequiresAuthorizationCharge(
          cartResponse.requiresAuthorizationCharge
        );
        setPreviewCartUpdated(true);
      }
    };

    createPreviewCart();
  }, [
    ambassadorRedirectModalEnabled,
    state.plan,
    state.subscription_level,
    state.insurance_level,
    state.referralCode,
    state.addon_subscription,
    subscriptionData,
    eligibleInsuranceData
  ]);

  useEffect(() => {
    function beforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();

      // Included for legacy support, e.g. Chrome/Edge < 119
      e.returnValue = true;
    }

    window.addEventListener('beforeunload', beforeUnload);

    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, []);

  useOnMount(() => {
    const planName =
      state.plan?.slug || searchParams?.subscription || 'classique';
    const defaultPlan = subscriptionData.find((s) => s.slug === planName);

    signupActions.updateFormValues({
      plan: defaultPlan as SubscriptionProduct
    });

    // Capture UTM source for conditional auth charge logic.
    // Check URL params first, then fall back to persisted cookie
    // (set by UtmSourceCapture on any landing page).
    const utmSource = searchParams?.utm_source;
    const resolvedUtmSource = utmSource
      ? typeof utmSource === 'string'
        ? utmSource
        : utmSource[0]
      : getUtmSource();

    if (resolvedUtmSource && !state.utm_source) {
      signupActions.updateFormValues({
        utm_source: resolvedUtmSource
      });
    }

    // Initiate Datadog RUM, if not already initiated
    // (Users going through signup are the only non-logged-in users we want to monitor)
    initializeDatadogRUM();

    if (stepIndex === 0) {
      console.warn('[segment] Application Start', {
        application_flow: applicationFlow
      });
      analytics.track('Application Start', {
        application_flow: applicationFlow
      });
    }
  });

  const shouldRedirectToAccount =
    stepIndex === 4 && !signupActions.formIsComplete();

  // If trying to load last page of application and form isn't complete,
  // redirect to first step to force completion of all fields.
  // The single-page flow's "first step" is /apply itself — sending it to
  // /apply/account would only bounce through a route that no longer exists
  // for that flow.
  useEffect(() => {
    if (shouldRedirectToAccount) {
      router.push(
        applicationFlow === APPLICATION_FLOW.SINGLE_PAGE
          ? '/apply'
          : SIGNUP_ROUTES.ACCOUNT
      );
    }
  }, [router, shouldRedirectToAccount, applicationFlow]);

  if (shouldRedirectToAccount) {
    return <></>; // Don't render the last step so avoid flashing empty state
  }

  if (user) {
    return null;
  }

  return (
    <div className={cn('SignUpApp', className)}>
      <div className='shell-contained'>
        <Elements
          stripe={stripePromise}
          options={stripeOptions}
        >
          <SignupForm
            subscriptionData={subscriptionData}
            insuranceData={eligibleInsuranceData}
            planSelected={state.plan}
            currentStep={stepIndex}
            applicationFlow={applicationFlow}
            user={state}
            previewCart={previewCart}
            referralCopy={referralCopy}
            stripeCouponId={stripeCouponId}
            requiresAuthorizationCharge={requiresAuthorizationCharge}
          />
        </Elements>
      </div>
      {stepIndex > 0 && stepIndex < 4 && (
        <>
          <SignUpSummaryBar
            selectedPlan={state.plan}
            subs={subscriptionData}
            insuranceData={eligibleInsuranceData}
            user={state}
            previewCart={previewCart}
            referralCopy={referralCopy}
          />
        </>
      )}
    </div>
  );
}

export default SignUpApp;
